import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by updating the current media gallery safely.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const currentSlideshow = this.querySelector('slideshow-component, [data-slideshow], .slideshow');
    const newSlideshow = newMediaGallery.querySelector('slideshow-component, [data-slideshow], .slideshow');

    // Se não existe galeria nova, não mexe
    if (!newSlideshow) return;

    const newMediaItems = newSlideshow.querySelectorAll(
      '[data-media-id], .media, img, video, model-viewer, iframe'
    );

    // Se não existe mídia nova visível, mantém a atual
    if (!newMediaItems.length) return;

    // Se houver slideshow atual, troca só o conteúdo interno
    if (currentSlideshow) {
      currentSlideshow.innerHTML = newSlideshow.innerHTML;
    } else {
      const currentContent = this.querySelector('[data-media-gallery-content], .product__media, .product__media-list');
      const newContent = newMediaGallery.querySelector('[data-media-gallery-content], .product__media, .product__media-list');

      if (currentContent && newContent) {
        const hasRenderableMedia = newContent.querySelector(
          'img, video, model-viewer, iframe, [data-media-id], .media'
        );

        if (!hasRenderableMedia) return;
        currentContent.innerHTML = newContent.innerHTML;
      }
    }

    // Se a variante tiver mídia destacada, tenta ativá-la
    const featuredMediaId = event.detail?.variant?.featured_media?.id;
    if (featuredMediaId && typeof this.setActiveMedia === 'function') {
      const sectionId = this.dataset.section;
      if (sectionId) {
        this.setActiveMedia(`${sectionId}-${featuredMediaId}`, true);
      }
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
